import { Divider, Grid, Hidden } from "@material-ui/core";
import { UISref } from "@uirouter/react";
import { noop } from "lodash";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { mixed, number, object } from "yup";
import { InfoButton, PrimaryButton } from "../../../components/button";
import { NumberField } from "../../../components/forms/number";
import { Flex } from "../../../components/layout";
import { Widget } from "../../../components/widget";
import { BillingDTO } from "../../../store/billing/models/dto";
import { Billing } from "../../../store/billing/reducer";
import { AllowedBlockchains } from "../../../store/chain/models/internal";
import { useFetchable } from "../../../store/common/fetchable";
import { isFetchableSuccess } from "../../../store/common/fetchable/helpers";
import {
  requestChainChange,
  useBrowserWallet,
} from "../../../store/wallets/crypto";
import { chainsMap } from "../../../store/wallets/crypto/chain-config";
import { toDecimal, useYupValidationResolver } from "../../../utils/utils";
import { BrowserWallet } from "../../wallets/all";
import { BillingRoutes } from "../routes";
import { Checkout } from "./checkout";
import { AMOUNT_OPTIONS, PurchaseForm } from "./common";

const CssGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1em;
`;

export const StyledFlex = styled(Flex)`
  justify-content: space-between;
  margin-bottom: 3em;
`;

export const BaseLineFlex = styled(Flex).attrs({ center: true })`
  align-items: baseline;
`;

const ChainDesc = styled.div`
  text-align: center;
`;

const Amount = styled.div`
  margin-right: 1em;
  font-size: 1.4em;
  font-weight: 300;
`;

const DividerWrapper = styled.div`
  padding: 0 2em;
`;

export const Cart: FC<{}> = () => {
  const skrtAddressesFetch = useFetchable(Billing.skrtAddresses);
  const balanceFetch = useFetchable(Billing.balance);

  const { accounts } = useBrowserWallet();

  const validator = useMemo(
    () =>
      object({
        amount: number().max(3000).min(0),
        chain: isFetchableSuccess(skrtAddressesFetch)
          ? mixed().oneOf([...skrtAddressesFetch.data.map((x) => x.chain)])
          : mixed(),
      }),
    [skrtAddressesFetch]
  );

  const resolver = useYupValidationResolver(validator);
  const {
    control,
    setValue,
    watch,
    getValues,
    trigger,
  } = useForm<PurchaseForm>({
    resolver,
    defaultValues: {
      amount: 50,
      chain: accounts.fold(
        () => "ETH",
        ({ networkId }) => chainsMap[networkId]
      ),
    },
  });

  const [propagatedValues, setPropagatedValues] = useState<{
    chain: BillingDTO.Responses.GetSKRTAddresses.T[number] | undefined;
    amount: number;
  }>({
    amount: getValues("amount"),
    chain: isFetchableSuccess(skrtAddressesFetch)
      ? skrtAddressesFetch.data.find(
          (x) =>
            x.chain ===
            accounts.fold(
              () => "ETH",
              ({ networkId }) => chainsMap[networkId]
            )
        )
      : undefined,
  });

  useEffect(() => {
    setValue(
      "chain",
      accounts.fold(
        () => "ETH",
        ({ networkId }) => chainsMap[networkId]
      )
    );

    setPropagatedValues({
      amount: propagatedValues.amount,
      chain: isFetchableSuccess(skrtAddressesFetch)
        ? skrtAddressesFetch.data.find(
            (x) =>
              x.chain ===
              accounts.fold(
                () => "ETH",
                ({ networkId }) => chainsMap[networkId]
              )
          )
        : undefined,
    });
  }, [accounts, skrtAddressesFetch]);

  const [highlightedAmount, setHighlightedAmount] = useState<
    typeof AMOUNT_OPTIONS[number] | undefined
  >(50);

  const selectedChain = watch("chain");
  const selectedAmount = watch("amount");

  useEffect(() => {
    setHighlightedAmount(
      AMOUNT_OPTIONS.includes(selectedAmount as any)
        ? (selectedAmount as typeof AMOUNT_OPTIONS[number])
        : undefined
    );

    validator
      .validate({
        amount: selectedAmount,
        chain: selectedChain,
      })
      .then(() => {
        setPropagatedValues({
          amount: selectedAmount,
          chain: isFetchableSuccess(skrtAddressesFetch)
            ? skrtAddressesFetch.data.find((x) => x.chain === selectedChain)
            : undefined,
        });
      })
      .catch(noop);
    trigger();
  }, [selectedChain, selectedAmount, skrtAddressesFetch]);

  const attemptChainChange = useCallback(
    async (chainId: AllowedBlockchains) => {
      await requestChainChange(chainId);
      setValue("chain", chainId);
    },
    []
  );

  const { t } = useTranslation("billing");

  return (
    <Widget>
      <StyledFlex>
        {isFetchableSuccess(balanceFetch) ? (
          <div>
            <BrowserWallet.Address>
              {t("your-current-balance")}
            </BrowserWallet.Address>
            <BrowserWallet.Balance>
              {toDecimal(balanceFetch.data.credits)}{" "}
              {t("credit_other", { ns: "common" })}
            </BrowserWallet.Balance>
          </div>
        ) : null}
        <UISref to={BillingRoutes.names.pricing}>
          <a>
            <PrimaryButton>{t("pricing")}</PrimaryButton>
          </a>
        </UISref>
      </StyledFlex>
      <Grid container spacing={6}>
        <Hidden mdDown>
          <Grid item lg={1} />
        </Hidden>

        <Grid item xs={12} md={6} lg={5}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <BaseLineFlex>
                <Amount>{t("credit", { ns: "common", count: 2 })}: </Amount>
                <Controller
                  control={control}
                  name="amount"
                  render={({ field, fieldState: { error } }) => (
                    <NumberField
                      fullWidth
                      {...field}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </BaseLineFlex>
            </Grid>
            <Grid item xs={12}>
              <CssGrid>
                {AMOUNT_OPTIONS.map((x) => (
                  <InfoButton
                    variant={highlightedAmount === x ? "contained" : "outlined"}
                    key={x}
                    onClick={() => setValue("amount", x)}
                  >
                    {x}
                  </InfoButton>
                ))}
              </CssGrid>
            </Grid>
            <Grid item xs={12}>
              <DividerWrapper>
                <Divider />
              </DividerWrapper>
            </Grid>
            {isFetchableSuccess(skrtAddressesFetch) ? (
              <>
                <Grid item xs={12}>
                  <ChainDesc>{t("choose-chain-for-payment")}:</ChainDesc>
                </Grid>
                <Grid item xs={12}>
                  <CssGrid>
                    {skrtAddressesFetch.data.map(({ chain, id }) => (
                      <InfoButton
                        variant={
                          selectedChain === chain ? "contained" : "outlined"
                        }
                        key={id}
                        onClick={() => attemptChainChange(chain)}
                      >
                        {chain}
                      </InfoButton>
                    ))}
                  </CssGrid>
                </Grid>{" "}
              </>
            ) : null}
          </Grid>
        </Grid>
        <Grid item xs={12} md={6} lg={5}>
          {propagatedValues.chain === undefined ? null : (
            <Checkout
              amount={propagatedValues.amount}
              chain={propagatedValues.chain}
            />
          )}
        </Grid>
        <Hidden mdDown>
          <Grid item lg={1} />
        </Hidden>
      </Grid>
    </Widget>
  );
};
